import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, ThumbsUp, Eye, Star } from "lucide-react";
import { CATEGORIES, catLabel, catAccent } from "@/lib/api";

export default function AdminAnalytics({ articles }) {
  const catData = CATEGORIES.map((c) => ({
    name: c.hi,
    id: c.id,
    count: articles.filter((a) => a.category === c.id).length,
  }));
  const topViewed = [...articles].sort((a, b) => b.views - a.views).slice(0, 5);
  const totalLikes = articles.reduce((s, a) => s + (a.likes || 0), 0);
  const featured = articles.filter((a) => a.featured).length;

  return (
    <div className="space-y-8" data-testid="admin-analytics">
      <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
        {[
          { icon: ThumbsUp, label: "Total Likes", value: totalLikes.toLocaleString("en-IN") },
          { icon: Star, label: "Featured Stories", value: featured },
        ].map((s) => (
          <div key={s.label} className="bg-[#0D0D10] p-5">
            <s.icon size={16} className="text-[#FFD400]" />
            <p className="font-display text-2xl text-white mt-3">{s.value}</p>
            <p className="font-mono-meta text-[10px] text-slate-500 uppercase tracking-widest mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="border border-white/10 p-6">
        <h3 className="font-editorial font-bold text-white text-lg mb-6 flex items-center gap-2">
          <TrendingUp size={16} className="text-[#FFD400]" /> Articles by Category
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={catData} margin={{ top: 4, right: 8, left: -24, bottom: 4 }}>
              <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={{ stroke: "rgba(255,255,255,0.15)" }} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: "rgba(255,212,0,0.06)" }}
                contentStyle={{ background: "#15151A", border: "1px solid rgba(255,255,255,0.15)", color: "#F8FAFC", fontSize: 12 }}
                labelStyle={{ color: "#FFD400" }}
              />
              <Bar dataKey="count" name="Articles" radius={[2, 2, 0, 0]}>
                {catData.map((d) => (
                  <Cell key={d.id} fill={catAccent(d.id)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-white/10">
        <h3 className="font-editorial font-bold text-white text-lg px-6 py-4 border-b border-white/10 flex items-center gap-2">
          <Eye size={16} className="text-[#FFD400]" /> Most Read Articles
        </h3>
        {topViewed.length === 0 ? (
          <p className="p-6 text-sm text-slate-400">No articles yet.</p>
        ) : (
          <div className="divide-y divide-white/[0.06]">
            {topViewed.map((a, i) => (
              <div key={a.id} className="flex items-center gap-4 px-6 py-3.5">
                <span className="font-display text-xl text-[#FFD400] w-8">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{a.title}</p>
                  <p className="font-mono-meta text-[10px] text-slate-500 mt-0.5">{catLabel(a.category)}</p>
                </div>
                <span className="font-mono-meta text-xs text-slate-400 flex items-center gap-1.5">
                  <Eye size={11} /> {a.views.toLocaleString("en-IN")}
                </span>
                <span className="font-mono-meta text-xs text-slate-400 flex items-center gap-1.5">
                  <ThumbsUp size={11} /> {(a.likes || 0).toLocaleString("en-IN")}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
